import React, { useContext, useEffect, useState } from 'react';
import t from 'react-translate';
import AdvancedSettingsSwitch from 'institutions/components/advanced-settings-switch';
import { AngularContext } from 'react-app';
import { useAppDispatch } from 'redux/store';
import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { config } from '@config/pendo.config.json';
import { config as appConfig } from '@config/config.json';
import SectionContent from '../section-content';

import AILearningAssistantToggle from './ai-learning-assistant-toggle';
import RoleplayUsageManagementCard from './roleplay-usage-management-card';

export const GEN_AI_ARTICLE_LINK = `${appConfig.helpLink}/article/ART-1631`;

interface ToggleGenerativeAiPayload {
  hasAiComponentGeneration: boolean;
  hasAiLearningAssistant: boolean;
}

const GenerativeAiCapabilities = () => {
  const { injectServices } = useContext(AngularContext);
  const [InstitutionsManager] = injectServices(['InstitutionsManager']);
  const dispatch = useAppDispatch();
  const [hasAiComponentGeneration, setHasAiComponentGeneration] = useState(InstitutionsManager.institution.hasAiComponentGeneration);
  const [form, setForm] = useState<ToggleGenerativeAiPayload>({
    hasAiComponentGeneration: InstitutionsManager.institution.hasAiComponentGeneration,
    hasAiLearningAssistant: InstitutionsManager.institution.hasAiLearningAssistant || false,
  });

  useEffect(() => {
    setHasAiComponentGeneration(InstitutionsManager.institution.hasAiComponentGeneration);
    setForm({
      hasAiComponentGeneration: InstitutionsManager.institution.hasAiComponentGeneration,
      hasAiLearningAssistant: InstitutionsManager.institution.hasAiLearningAssistant || false,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const switchGenerativeAiCapabilities = (updatedForm = form) => {
    InstitutionsManager.institution.updateFromReact(updatedForm);

    const failureCallback = () => {
      dispatch(addAlertMessage({ type: AlertMessageType.ERROR, header: t.FORM.OOPS(), message: t.FORM.ERROR_SOMETHING_WRONG() }));
      // Revert the changes on failure
      InstitutionsManager.institution.updateFromReact({
        hasAiComponentGeneration: !updatedForm.hasAiComponentGeneration,
        hasAiLearningAssistant: !updatedForm.hasAiLearningAssistant,
      });
      // Revert local state as well
      setForm(form);
    };

    InstitutionsManager.toggleGenerativeAiCapabilities(updatedForm)
      .then((success) => {
        if (!success) {
          failureCallback();
        }
      }, () => {
        failureCallback();
      });
  };

  const handleAiComponentGenerationChange = () => {
    const updatedForm = {
      hasAiLearningAssistant: !form.hasAiComponentGeneration,
      hasAiComponentGeneration: !form.hasAiComponentGeneration,
    };
    setForm(updatedForm);
    setHasAiComponentGeneration(updatedForm.hasAiComponentGeneration);
    switchGenerativeAiCapabilities(updatedForm);
  };

  const handleAiLearningAssistantChange = (value: boolean) => {
    const updatedForm = {
      ...form,
      hasAiLearningAssistant: value,
    };
    setForm(updatedForm);
    switchGenerativeAiCapabilities(updatedForm);
  };

  return (
    <SectionContent
      header={t.INSTITUTIONS.ADVANCED_SETTINGS.GENERATIVE_AI_CAPABILITIES.TITLE()}
      description={[t.INSTITUTIONS.ADVANCED_SETTINGS.GENERATIVE_AI_CAPABILITIES.DESCRIPTION(GEN_AI_ARTICLE_LINK)]}
      extras={(
        <>
          <div className='d-flex align-self-start'>
            <AdvancedSettingsSwitch
              onChange={handleAiComponentGenerationChange}
              dataQa={config.pendo.settings.toggleGenerativeAiCapabilities}
              value='vm.InstitutionsManager.institution.hasAiComponentGeneration'
            />
          </div>
          {hasAiComponentGeneration && (
            <div className='mt-5 p-4 bg-gray-7 border border-gray-5 rounded'>
              <RoleplayUsageManagementCard />
              <hr className='my-4 border-gray-5' />
              <AILearningAssistantToggle
                onChange={handleAiLearningAssistantChange}
                value={form.hasAiLearningAssistant}
              />
            </div>
          )}
        </>
      )}
    />
  );
};

export default GenerativeAiCapabilities;
